const DOCUMENT_POSITION_PRECEDING =
    typeof Node !== "undefined" ? Node.DOCUMENT_POSITION_PRECEDING : 2;
const DOCUMENT_POSITION_FOLLOWING =
    typeof Node !== "undefined" ? Node.DOCUMENT_POSITION_FOLLOWING : 4;

let imageStates = [];
let registrationCounter = 0;
const listeners = new Set();

/**
 * Register a background image in the preload queue.
 * Supports both object signature ({ id, element, priority, intersecting, isLoaded, canLoad, setCanLoad })
 * and positional signature (id, element, setCanLoad, options).
 */
export const registerImagePlayer = (param1, element, setCanLoad, options = {}) => {
    let instance;
    if (typeof param1 === "object" && param1 !== null) {
        instance = {
            id: param1.id,
            element: param1.element || null,
            priority: Boolean(param1.priority),
            intersecting: Boolean(param1.intersecting),
            isLoaded: Boolean(param1.isLoaded),
            canLoad: Boolean(param1.canLoad),
            setCanLoad: param1.setCanLoad || (() => {}),
            order: ++registrationCounter,
        };
    } else {
        instance = {
            id: param1,
            element: element || null,
            priority: Boolean(options.priority),
            intersecting: Boolean(options.intersecting),
            isLoaded: Boolean(options.isLoaded),
            canLoad: Boolean(options.canLoad),
            setCanLoad: setCanLoad || (() => {}),
            order: ++registrationCounter,
        };
    }

    imageStates.push(instance);
    updatePreloadQueue();
    return instance;
};

/**
 * Unregister a background image from the preload queue.
 */
export const unregisterImagePlayer = (id) => {
    imageStates = imageStates.filter((item) => item.id !== id);
    updatePreloadQueue();
};

/**
 * Update the status of an existing image (intersection, loaded state, etc.).
 */
export const updateImageStatus = (id, { intersecting, isLoaded, priority, element } = {}) => {
    const item = imageStates.find((p) => p.id === id);
    if (!item) {
        return;
    }

    let changed = false;
    if (intersecting !== undefined && item.intersecting !== intersecting) {
        item.intersecting = intersecting;
        changed = true;
    }
    if (isLoaded !== undefined && item.isLoaded !== isLoaded) {
        item.isLoaded = isLoaded;
        changed = true;
    }
    if (priority !== undefined && item.priority !== priority) {
        item.priority = priority;
        changed = true;
    }
    if (element !== undefined && item.element !== element) {
        item.element = element;
        changed = true;
    }

    if (changed) {
        updatePreloadQueue();
    }
};

/**
 * Reset queue state (primarily for tests).
 */
export const resetImagePreloadQueue = () => {
    imageStates = [];
    registrationCounter = 0;
    listeners.clear();
};

/**
 * Get current list of registered image states (defensive copy).
 */
export const getImageQueueState = () => [...imageStates];

/**
 * Helper to get hero image loading progress (1.0 = loaded, 0.0 = waiting).
 */
export const getHeroLoadProgress = () => {
    const hero = imageStates.find((item) => item.priority);
    if (!hero) {
        return 1.0;
    }
    return hero.isLoaded ? 1.0 : 0.0;
};

/**
 * Subscribe to preload queue changes. Returns unsubscribe function.
 */
export const subscribeImagePreloadQueue = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// -----------------------------------------------------------------------------
// Internal queue processing & sorting helpers
// -----------------------------------------------------------------------------

const updatePreloadQueue = () => {
    sortImages();
    evaluatePreloadPermissions();
    notifyQueueListeners();
};

const sortImages = () => {
    imageStates.sort((a, b) => {
        if (a.priority && !b.priority) return -1;
        if (!a.priority && b.priority) return 1;

        const elA = a.element;
        const elB = b.element;
        if (elA && elB && elA !== elB && typeof elA.compareDocumentPosition === "function") {
            try {
                const position = elA.compareDocumentPosition(elB);
                if (position & DOCUMENT_POSITION_FOLLOWING) {
                    return -1;
                }
                if (position & DOCUMENT_POSITION_PRECEDING) {
                    return 1;
                }
            } catch (e) {
                // Ignore DOM errors and fall back to registration order
            }
        }

        return (a.order || 0) - (b.order || 0);
    });
};

const hasActiveImageLoading = () => {
    for (let i = 0; i < imageStates.length; i++) {
        const item = imageStates[i];
        if ((item.priority || item.intersecting) && !item.isLoaded) {
            return true;
        }
    }
    return false;
};

const enableCanLoad = (item) => {
    if (!item.canLoad) {
        item.canLoad = true;
        try {
            item.setCanLoad(true);
        } catch (e) {
            // Ignore unmounted/setter exceptions
        }
    }
};

const evaluatePreloadPermissions = () => {
    // 1. Any image with priority === true or intersecting === true immediately gets canLoad = true
    for (let i = 0; i < imageStates.length; i++) {
        const item = imageStates[i];
        if (item.priority || item.intersecting) {
            enableCanLoad(item);
        }
    }

    // 2. If no currently intersecting/priority image is still loading (!isLoaded),
    // look ahead and set canLoad = true for the next 2 unloaded images in DOM order.
    if (!hasActiveImageLoading()) {
        let lookaheadCount = 0;
        for (let i = 0; i < imageStates.length; i++) {
            const item = imageStates[i];
            if (!item.isLoaded) {
                enableCanLoad(item);
                lookaheadCount++;
                if (lookaheadCount >= 2) {
                    break;
                }
            }
        }
    }
};

const notifyQueueListeners = () => {
    listeners.forEach((listener) => {
        try {
            listener([...imageStates]);
        } catch (e) {
            // Protect queue from subscriber exceptions
        }
    });
};
